import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Version Service - NO FALLBACKS
 *
 * Retrieves version information for Students & Lecturers Portal from Azure Blob Storage via backend API.
 * This service does NOT provide fallback values - if the version cannot be loaded,
 * it will throw an error.
 *
 * The backend ensures there is always exactly ONE version file in the releasenotes_portal folder.
 */
@Injectable({
  providedIn: 'root',
})
export class VersionService {
  private http = inject(HttpClient);
  private readonly API_BASE_URL = environment.api.apiUrl;

  // Cache for version content
  private versionCache = signal<string | null>(null);
  private versionLoading = signal<boolean>(false);
  private versionError = signal<string | null>(null);

  /**
   * Get current portal version from Azure Blob Storage (NO FALLBACK)
   *
   * Returns cached value if available, otherwise fetches from backend.
   *
   * @returns Observable<string> - The version content (e.g., "2026.01.00")
   * @throws Error if version cannot be loaded
   */
  getCurrentVersion(): Observable<string> {
    // Return cached version if available
    const cached = this.versionCache();
    if (cached) {
      return new Observable((observer) => {
        observer.next(cached);
        observer.complete();
      });
    }

    // Check if already loading - throw error to prevent multiple concurrent requests
    if (this.versionLoading()) {
      return throwError(() => new Error('Version is already being loaded'));
    }

    this.versionLoading.set(true);
    this.versionError.set(null);

    return this.http
      .get(`${this.API_BASE_URL}/api/app/version/portal/current`, {
        responseType: 'text',
      })
      .pipe(
        tap((version) => {
          this.versionCache.set(version);
          this.versionLoading.set(false);
          console.log('✅ Portal version loaded from blob storage:', version);
        }),
        catchError((error) => {
          this.versionLoading.set(false);
          const errorMessage = `Failed to load portal version from blob storage: ${error.status} ${error.statusText}`;
          this.versionError.set(errorMessage);
          console.error('❌', errorMessage, error);
          // NO FALLBACK - throw the error
          return throwError(() => new Error(errorMessage));
        }),
      );
  }

  /**
   * Get current portal version file information (name + content + latest patch version)
   *
   * @returns Observable with version file info
   * @throws Error if version info cannot be loaded
   */
  getCurrentVersionFileInfo(): Observable<{
    version_file: string;
    version_content: string;
    latest_patch_version: string;
  }> {
    return this.http
      .get<{
        version_file: string;
        version_content: string;
        latest_patch_version: string;
      }>(`${this.API_BASE_URL}/api/app/version/portal/current/file`)
      .pipe(
        catchError((error) => {
          const errorMessage = `Failed to load portal version file info: ${error.status} ${error.statusText}`;
          console.error('❌', errorMessage, error);
          return throwError(() => new Error(errorMessage));
        }),
      );
  }

  /**
   * Fetch release notes HTML content from API
   *
   * @param fileName - Version file name (e.g. "2026.09.01.html")
   */
  fetchReleaseNotes(fileName: string): Observable<string> {
    const file = fileName.endsWith('.html') ? fileName : `${fileName}.html`;
    return this.http.get(`${this.API_BASE_URL}/api/app/releasenotes_portal/${file}`, {
      responseType: 'text',
    });
  }

  /**
   * Open release notes in a new browser tab without exposing raw API link, identical to Aetheris SIS app
   *
   * @param fileName - Version file name (e.g. "2026.09.01.html")
   */
  async openReleaseNotesInNewTab(fileName: string): Promise<void> {
    if (!fileName) {
      return;
    }

    // Open target window immediately to avoid popup blocker blocking async window.open
    const newWindow = window.open('about:blank', '_blank');
    if (newWindow) {
      newWindow.document.title = 'Loading Release Notes...';
      newWindow.document.body.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui,-apple-system,sans-serif;color:#64748b;background:#0f172a;">
          <div style="text-align:center;">
            <div style="display:inline-block;width:32px;height:32px;border:3px solid rgba(255,255,255,0.2);border-radius:50%;border-top-color:#38bdf8;animation:spin 1s ease-in-out infinite;"></div>
            <p style="margin-top:16px;font-size:14px;letter-spacing:0.05em;">Loading Release Notes...</p>
            <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
          </div>
        </div>
      `;
    }

    try {
      const { firstValueFrom } = await import('rxjs');
      const htmlContent = await firstValueFrom(this.fetchReleaseNotes(fileName));
      if (newWindow && !newWindow.closed) {
        newWindow.document.open();
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      }
    } catch (error) {
      if (newWindow && !newWindow.closed) {
        newWindow.document.title = 'Error Loading Release Notes';
        newWindow.document.body.innerHTML = `
          <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui,-apple-system,sans-serif;color:#ef4444;background:#0f172a;">
            <div style="text-align:center;max-width:480px;padding:24px;">
              <h2 style="font-size:18px;margin-bottom:8px;">Unable to load release notes</h2>
              <p style="font-size:13px;color:#94a3b8;">${error instanceof Error ? error.message : 'Please check your connection and try again.'}</p>
            </div>
          </div>
        `;
      }
    }
  }

  /**
   * Clear version cache (e.g., after upload)
   */
  clearCache(): void {
    this.versionCache.set(null);
    this.versionLoading.set(false);
    this.versionError.set(null);
  }

  /**
   * Get cached version (read-only)
   */
  getCachedVersion(): string | null {
    return this.versionCache();
  }

  /**
   * Check if version is currently loading
   */
  isLoading(): boolean {
    return this.versionLoading();
  }

  /**
   * Get current error message if any
   */
  getError(): string | null {
    return this.versionError();
  }
}
